import {
  mutationOptions,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';

import {
  createCampaign,
  createCampaignAnalysis,
  deleteCampaign,
  deleteCampaignAnalysis,
  endCampaignDate,
  excludeCampaignPosts,
  updateCampaign,
  updateCampaignCreatorLabel,
  updateCampaignName,
} from './generated/api';
import { updateCampaignAnalysisResultTitle } from './generated/api/update-campaign-analysis-result-title';
import type {
  CampaignAnalysisHistoryResponse,
  CampaignAnalysisResult,
  CreateCampaignAnalysisRequest,
  CreateCampaignRequest,
  UpdateCampaignRequest,
} from './generated/campaign-type';
import { campaignQueryKeys } from './query-keys';

export type ExecuteCampaignAnalysisMutationRequest = {
  campaignId: string;
  request: CreateCampaignAnalysisRequest;
};

export type DeleteCampaignAnalysisMutationRequest = {
  campaignId: string;
  runId: string;
};

export type RenameCampaignAnalysisMutationRequest = DeleteCampaignAnalysisMutationRequest & {
  title: string;
};

export type RenameCampaignMutationRequest = {
  campaignId: string;
  campaignName: string;
};

export type ExcludeCampaignPostsMutationRequest = {
  campaignId: string;
  postIds: string[];
};

export type UpdateCampaignCreatorLabelMutationRequest = {
  campaignId: string;
  creatorId: string;
  label: string | null;
};

export type UpdateCampaignMutationRequest = {
  campaignId: string;
  request: UpdateCampaignRequest;
};

export type InvalidateCampaignQueriesOptions = {
  campaignId?: string;
  includeDetail?: boolean;
  includeMetrics?: boolean;
};

export async function invalidateCampaignQueries(
  queryClient: QueryClient,
  workspaceId: string,
  {
    campaignId,
    includeDetail = false,
    includeMetrics = false,
  }: InvalidateCampaignQueriesOptions = {},
) {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: campaignQueryKeys.all(workspaceId) }),
  ];

  if (campaignId && includeDetail) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.detail(workspaceId, campaignId),
      }),
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.overview(workspaceId, campaignId),
      }),
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.creatorsRoot(workspaceId, campaignId),
      }),
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.creatorCountsRoot(workspaceId, campaignId),
      }),
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.postsRoot(workspaceId, campaignId),
      }),
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.postCountsRoot(workspaceId, campaignId),
      }),
    );
  }

  if (campaignId && includeMetrics) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.metrics(workspaceId, campaignId),
      }),
    );
  }

  await Promise.all(invalidations);
}

export function createCampaignMutationOptions(queryClient: QueryClient, workspaceId: string) {
  return mutationOptions({
    mutationFn: (request: CreateCampaignRequest) =>
      createCampaign({
        path: { workspaceId },
        body: request,
      }),
    onSuccess: () => invalidateCampaignQueries(queryClient, workspaceId),
  });
}

export function deleteCampaignMutationOptions(queryClient: QueryClient, workspaceId: string) {
  return mutationOptions({
    mutationFn: (campaignId: string) => deleteCampaign({ path: { campaignId } }),
    onSuccess: () => invalidateCampaignQueries(queryClient, workspaceId),
  });
}

export function endCampaignMutationOptions(queryClient: QueryClient, workspaceId: string) {
  return mutationOptions({
    mutationFn: ({ campaignId }: { campaignId: string }) =>
      endCampaignDate({ path: { campaignId } }),
    onSuccess: () => invalidateCampaignQueries(queryClient, workspaceId),
  });
}

export function excludeCampaignPostsMutationOptions(queryClient: QueryClient, workspaceId: string) {
  return mutationOptions({
    mutationFn: ({ campaignId, postIds }: ExcludeCampaignPostsMutationRequest) =>
      excludeCampaignPosts({
        path: { campaignId },
        body: { campaign_post_ids: postIds },
      }),
    onSuccess: (_data, { campaignId }) =>
      invalidateCampaignQueries(queryClient, workspaceId, {
        campaignId,
        includeDetail: true,
        includeMetrics: true,
      }),
  });
}

export function executeCampaignAnalysisMutationOptions(
  queryClient: QueryClient,
  workspaceId: string,
  workspaceUsageQueryKey: QueryKey,
) {
  return mutationOptions({
    mutationFn: ({ campaignId, request }: ExecuteCampaignAnalysisMutationRequest) =>
      createCampaignAnalysis({ path: { campaignId }, body: request }),
    onSuccess: async (_response, { campaignId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: campaignQueryKeys.analysisList(workspaceId, campaignId),
        }),
        queryClient.invalidateQueries({ queryKey: workspaceUsageQueryKey }),
      ]);
    },
  });
}

export function deleteCampaignAnalysisMutationOptions(
  queryClient: QueryClient,
  workspaceId: string,
) {
  return mutationOptions({
    mutationFn: ({ campaignId, runId }: DeleteCampaignAnalysisMutationRequest) =>
      deleteCampaignAnalysis({ path: { campaignId, runId } }),
    onSuccess: (_response, { campaignId, runId }) => {
      const queryKey = campaignQueryKeys.analysisList(workspaceId, campaignId);
      queryClient.setQueryData<InfiniteData<CampaignAnalysisHistoryResponse, string | undefined>>(
        queryKey,
        (history) =>
          history
            ? {
                ...history,
                pages: history.pages.map((page) => ({
                  ...page,
                  items: page.items.filter((run) => run.run_id !== runId),
                })),
              }
            : history,
      );
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function renameCampaignAnalysisMutationOptions(
  queryClient: QueryClient,
  workspaceId: string,
) {
  return mutationOptions({
    mutationFn: ({ campaignId, runId, title }: RenameCampaignAnalysisMutationRequest) =>
      updateCampaignAnalysisResultTitle({
        path: { campaignId, runId },
        body: { title },
      }),
    onSuccess: (_response, { campaignId, runId, title }) => {
      queryClient.setQueryData<CampaignAnalysisResult>(
        campaignQueryKeys.analysisResult(workspaceId, campaignId, runId),
        (result) =>
          result
            ? {
                ...result,
                meta: { ...result.meta, title },
              }
            : result,
      );
      void queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.analysisList(workspaceId, campaignId),
      });
    },
  });
}

export function renameCampaignMutationOptions(queryClient: QueryClient, workspaceId: string) {
  return mutationOptions({
    mutationFn: ({ campaignId, campaignName }: RenameCampaignMutationRequest) =>
      updateCampaignName({ path: { campaignId }, body: { name: campaignName } }),
    onSuccess: () => invalidateCampaignQueries(queryClient, workspaceId),
  });
}

export function updateCampaignMutationOptions() {
  return mutationOptions({
    mutationFn: ({ campaignId, request }: UpdateCampaignMutationRequest) =>
      updateCampaign({
        path: { campaignId },
        body: request,
      }),
  });
}

export function updateCampaignCreatorLabelMutationOptions(
  queryClient: QueryClient,
  workspaceId: string,
) {
  return mutationOptions({
    mutationFn: ({ campaignId, creatorId, label }: UpdateCampaignCreatorLabelMutationRequest) =>
      updateCampaignCreatorLabel({
        path: { campaignId, campaignCreatorId: creatorId },
        body: { label } as Parameters<typeof updateCampaignCreatorLabel>[0]['body'],
      }),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.creatorsRoot(workspaceId, variables.campaignId),
        refetchType: 'none',
      });
      void queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.creatorCountsRoot(workspaceId, variables.campaignId),
        refetchType: 'none',
      });
    },
  });
}
